import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import trLocale from 'date-fns/locale/tr';
import { useCallback, useRef, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { ClassicEditor } from 'ckeditor5';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EditIcon from '@mui/icons-material/Edit';
import { getEmptyProduct, Product } from '../../interfaces/Product/Product';
import { sharedStyles } from '../../utils/Style';
import { RichTextEditor } from '../../components/RichTextEditor/RichTextEditor';
import NumberInput from '../../components/NumberInput/NumberInput';
import { BrandDropdown } from '../../components/Dropdowns/BrandDropdown';
import { CategoriesDropdown } from '../../components/Dropdowns/CategoriesDropdown';
import { ProductFiltersDropdown } from '../../components/Dropdowns/ProductFiltersDropdown';
import { TagsDropdown } from '../../components/Dropdowns/TagsDropdown';
import { TaxDropdown } from '../../components/Dropdowns/TaxDropdown';
import { UpdateProductStockDialog } from './UpdateProductStockDialog';
import { TranslationAdornment } from '../../components/Translation/TranslationAdornment';

const width = 750;

interface ModifyProductFieldsProps {
  initialValue?: Product;
  onSubmit: (product: Product) => void;
}

export const ModifyProductFields = (props: ModifyProductFieldsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<CKEditor<ClassicEditor>>(null);

  const [product, setProduct] = useState(
    props.initialValue ?? getEmptyProduct(),
  );
  const [showStockDialog, setShowStockDialog] = useState(false);

  const handleSave = () => {
    props.onSubmit({
      ...product,
      description: editorRef.current?.editor?.getData() ?? '',
    });
  };

  const handlePriceChange = useCallback(
    (index: number, newPrice: number) => {
      const newPrices = [...product.pricing.prices];
      newPrices[index] = newPrice;

      setProduct({
        ...product,
        basePrice: index ? product.basePrice : newPrice,
        pricing: { ...product.pricing, prices: newPrices },
      });
    },
    [product],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'fit-content',
        gap: 1.5,
        minWidth: 0,
        alignItems: 'flex-start',
      }}
    >
      <UpdateProductStockDialog
        product={product}
        onUpdate={(x) => {
          setProduct({ ...product, quantity: x.quantity });
          setShowStockDialog(false);
        }}
        open={showStockDialog}
        onClose={() => setShowStockDialog(false)}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 1.5,
          minWidth: 0,
        }}
      >
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            p: 3,
            width,
          }}
        >
          <Typography
            sx={{
              ...sharedStyles.h5,
              fontWeight: 'bold',
            }}
          >
            Genel
          </Typography>
          <Divider />

          <TextField
            label='İsim'
            variant='outlined'
            size='small'
            value={product.name}
            onChange={(e) => setProduct({ ...product, name: e.target.value })}
            fullWidth
            InputProps={{
              endAdornment: (
                <TranslationAdornment
                  multiline
                  defaultValue={product.name}
                  value={product.nameTranslation}
                  onChange={(x) =>
                    setProduct({ ...product, nameTranslation: x })
                  }
                />
              ),
            }}
          />

          <TextField
            label='Kısa Açıklama'
            variant='outlined'
            size='small'
            multiline
            value={product.shortDescription}
            onChange={(e) =>
              setProduct({ ...product, shortDescription: e.target.value })
            }
            fullWidth
            InputProps={{
              endAdornment: (
                <TranslationAdornment
                  multiline
                  defaultValue={product.shortDescription}
                  value={product.shortDescriptionTranslation}
                  onChange={(x) =>
                    setProduct({ ...product, shortDescriptionTranslation: x })
                  }
                />
              ),
            }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Typography variant='h6'>Ürün Açıklaması</Typography>

            <TranslationAdornment
              richText
              defaultValue={product.description}
              value={product.descriptionTranslation}
              onChange={(x) =>
                setProduct({ ...product, descriptionTranslation: x })
              }
            />
          </Box>

          <Box ref={containerRef} sx={{ display: 'flex' }}>
            <RichTextEditor
              editorRef={editorRef}
              placeholder='Açıklama'
              initialData={product.description}
              width={width}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Box sx={{ width: 150, mr: 1.5 }}>
              <NumberInput
                label='Sıra'
                value={product.productOrder}
                onChange={(newOrder) =>
                  setProduct({ ...product, productOrder: newOrder })
                }
              />
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={product.isActive}
                  onChange={() =>
                    setProduct({
                      ...product,
                      isActive: !product.isActive,
                    })
                  }
                />
              }
              label={<Typography sx={sharedStyles.subtitle2}>Aktif</Typography>}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={product.isVirtual}
                  onChange={() =>
                    setProduct({
                      ...product,
                      isVirtual: !product.isVirtual,
                    })
                  }
                />
              }
              label={
                <Typography sx={sharedStyles.subtitle2}>
                  Sanal (Kargolanmayacak)
                </Typography>
              }
            />
          </Box>

          <BrandDropdown
            label='Marka'
            brandID={product.brandID}
            onChange={(id) => setProduct({ ...product, brandID: id })}
          />

          <CategoriesDropdown
            label='Kategori'
            categoryIDs={product.categoryIDs}
            onChange={(ids) => setProduct({ ...product, categoryIDs: ids })}
          />

          <ProductFiltersDropdown
            label='Filtre Değerleri'
            filterValueIDs={product.filterValueIDs}
            onChange={(ids) => setProduct({ ...product, filterValueIDs: ids })}
          />

          <TagsDropdown
            label='Etiketler'
            tagIDs={product.tagIDs}
            onChange={(ids) => setProduct({ ...product, tagIDs: ids })}
          />
        </Paper>

        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            width,
            p: 3,
            height: 'fit-content',
          }}
        >
          <Typography
            sx={{
              ...sharedStyles.h5,
              fontWeight: 'bold',
            }}
          >
            Envanter
          </Typography>
          <Divider />

          <TextField
            label='Barkod'
            variant='outlined'
            size='small'
            multiline
            value={product.barcode}
            onChange={(e) =>
              setProduct({ ...product, barcode: e.target.value })
            }
            fullWidth
          />

          <TextField
            label='SKU'
            variant='outlined'
            size='small'
            multiline
            value={product.sku ?? ''}
            onChange={(e) =>
              setProduct({ ...product, sku: e.target.value || undefined })
            }
            fullWidth
          />
          <TextField
            label='HB SKU'
            variant='outlined'
            size='small'
            multiline
            value={product.hepsiburada_sku}
            onChange={(e) =>
              setProduct({ ...product, hepsiburada_sku: e.target.value })
            }
            fullWidth
          />

          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
            <Tooltip
              title='Bu ürün varyantlara sahip olduğu için stoğu doğrudan değiştirilemez.'
              open={product.hasOptions ? undefined : false}
            >
              <Box sx={{ flexGrow: 1 }}>
                <NumberInput
                  label='Stok Adedi'
                  value={product.quantity}
                  onChange={(newQty) =>
                    setProduct({ ...product, quantity: newQty })
                  }
                  disabled={product.hasOptions || !!props.initialValue}
                />
              </Box>
            </Tooltip>

            {!!props.initialValue && !product.hasOptions && (
              <IconButton
                onClick={() => setShowStockDialog(true)}
                color='primary'
                sx={{ p: 0 }}
              >
                <EditIcon />
              </IconButton>
            )}

            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={trLocale}
            >
              <DatePicker
                label='Son Kullanma Tarihi'
                disabled={!product.expiryDate}
                onChange={(newExpiry) =>
                  setProduct({ ...product, expiryDate: newExpiry || undefined })
                }
                value={product.expiryDate ?? null}
                inputFormat='d MMMM yyyy'
                renderInput={(params) => (
                  <TextField {...params} size='small' fullWidth />
                )}
              />
            </LocalizationProvider>
            <Switch
              checked={!!product.expiryDate}
              onChange={() =>
                setProduct({
                  ...product,
                  expiryDate: product.expiryDate ? undefined : new Date(),
                })
              }
            />
          </Box>

          <Typography
            sx={{
              ...sharedStyles.h5,
              fontWeight: 'bold',
              mt: 1.5,
            }}
          >
            Fiyatlandırma
          </Typography>
          <Divider />

          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
            <Box sx={{ width: '40%' }}>
              <TaxDropdown
                label='Vergi'
                taxID={product.taxID}
                onChange={(tax) => setProduct({ ...product, taxID: tax })}
              />
            </Box>

            <NumberInput
              label='Özel Fiyat'
              value={product.pricing.specialPrice}
              onChange={(newPrice) =>
                setProduct({
                  ...product,
                  pricing: { ...product.pricing, specialPrice: newPrice },
                })
              }
              acceptFloat
              endAdornment={<Typography>₺</Typography>}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
            {product.pricing.prices.map((price, i) => (
              <NumberInput
                key={i}
                label={`Fiyat ${i + 1}`}
                value={price}
                onChange={(newPrice) => handlePriceChange(i, newPrice)}
                acceptFloat
                endAdornment={<Typography>₺</Typography>}
              />
            ))}
          </Box>
        </Paper>
      </Box>
      <Button
        variant='contained'
        onClick={handleSave}
        sx={{ ...sharedStyles.buttonText }}
      >
        Kaydet
      </Button>
    </Box>
  );
};
